<template>
  <!--
    Website and social links, one row per platform. Only platforms with a
    value start as rows; "Add social" opens another, and each row can be
    removed (which empties its value). Bound to one links object
    (`v-model`, see utils/subkreweLinkRule.ts); errors come from the parent
    form through `errorFor`, and `blur` reports which row was left.
  -->
  <div class="kb-form-fields">
    <div>
      <div class="kb-links-title">
        Website &amp; social media <span class="kb-field-optional">(optional)</span>
      </div>
      <p class="kb-field-hint m-0 mt-0.5">
        Shown to members browsing SubKrewes and may be published on chewbacchus.org.
        Paste a full link, or just an @handle for the social platforms.
      </p>
    </div>

    <div
      v-if="activePlatforms.length"
      class="kb-link-rows"
    >
      <KbField
        v-for="platform in activePlatforms"
        :key="platform.field"
        :field-id="idFor(platform.field)"
        :label="platform.label"
        :error="errorFor?.(platform.field)"
        class="kb-link-row"
        :data-field="platform.field"
      >
        <div class="flex items-center gap-1">
          <InputText
            :id="idFor(platform.field)"
            v-model="model[platform.field]"
            size="small"
            fluid
            :placeholder="platform.placeholder"
            autocomplete="off"
            :disabled="disabled"
            :invalid="Boolean(errorFor?.(platform.field))"
            @blur="$emit('blur', platform.field)"
          />
          <Button
            v-tooltip.top="{ value: `Remove ${platform.label}`, showDelay: 300 }"
            icon="pi pi-times"
            text
            rounded
            severity="secondary"
            size="small"
            type="button"
            :aria-label="`Remove ${platform.label}`"
            :disabled="disabled"
            @click="removePlatform(platform.field)"
          />
        </div>
      </KbField>
    </div>

    <div v-if="availablePlatforms.length && !disabled">
      <Button
        label="Add social"
        icon="pi pi-plus"
        size="small"
        text
        type="button"
        class="kb-links-add"
        @click="addMenu?.toggle($event)"
      />
      <Menu
        ref="addMenu"
        :model="addMenuItems"
        popup
      >
        <template #item="{ item }">
          <span class="flex items-center gap-2 px-3 py-2 cursor-pointer">
            <i
              v-if="item.iconName.startsWith('pi ')"
              :class="[item.iconName, '!text-base !leading-none']"
              aria-hidden="true"
            />
            <Icon
              v-else
              :name="item.iconName"
              class="!text-base !leading-none"
            />
            <span>{{ item.label }}</span>
          </span>
        </template>
      </Menu>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { MenuItem } from 'primevue/menuitem'
import { computed, nextTick, ref, watch } from 'vue'
import type { SubKreweLinkField } from '~/shared/utils/subkreweLinks.js'
import { SUBKREWE_LINK_PLATFORMS } from '~/shared/utils/subkreweLinks.js'
import type { SubKreweLinkValues } from '~/utils/subkreweLinkRule'

interface Props {
  /** Prefix for the inputs' ids: `${idPrefix}-website` etc. */
  idPrefix?: string
  /** The message to show under a row, from the parent form */
  errorFor?: (field: SubKreweLinkField) => string | undefined
  disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), { idPrefix: 'subkrewe-link', errorFor: undefined })

/**
 * `blur`: a row's input was left, so the form can check (and auto-save) it.
 * `removed`: a row was removed and its value emptied; a form editing a saved
 * SubKrewe saves that to clear the stored link.
 * `added`: a row was opened (its input has focus).
 * `pending`: whether any open row is still empty, i.e. was opened for the
 * captain to fill in and has nothing in it yet.
 */
const emit = defineEmits<{
  blur: [field: SubKreweLinkField]
  added: [field: SubKreweLinkField]
  removed: [field: SubKreweLinkField]
  pending: [hasUnsavedRows: boolean]
}>()

const model = defineModel<SubKreweLinkValues>({ required: true })

const idFor = (field: SubKreweLinkField) => `${props.idPrefix}-${field}`

const fieldsWithValue = (values: Partial<SubKreweLinkValues> | null | undefined): SubKreweLinkField[] =>
  SUBKREWE_LINK_PLATFORMS
    .filter(platform => !!values?.[platform.field]?.trim())
    .map(platform => platform.field)

// Only platforms with a link start visible; the rest are added on demand.
const shownFields = ref<SubKreweLinkField[]>(fieldsWithValue(model.value))
const removedFields = ref<SubKreweLinkField[]>([])

const addMenu = ref<{ toggle: (event: Event) => void } | null>(null)

// Reveal links that arrive later (the form taking in the saved SubKrewe),
// without resurrecting rows the captain just removed.
watch(() => model.value, (values) => {
  for (const field of fieldsWithValue(values)) {
    if (!shownFields.value.includes(field) && !removedFields.value.includes(field)) {
      shownFields.value.push(field)
    }
  }
}, { deep: true })

const activePlatforms = computed(() =>
  SUBKREWE_LINK_PLATFORMS.filter(platform => shownFields.value.includes(platform.field)),
)

// Rows the captain opened that have nothing in them yet.
const hasUnsavedRows = computed(() =>
  shownFields.value.some(field => !model.value?.[field]?.trim()),
)

watch(hasUnsavedRows, pending => emit('pending', pending), { immediate: true })

const availablePlatforms = computed(() =>
  SUBKREWE_LINK_PLATFORMS.filter(platform => !shownFields.value.includes(platform.field)),
)

interface LinkMenuItem extends MenuItem {
  label: string
  /** Rendered by the #item slot: a PrimeIcons class or an Iconify name */
  iconName: string
}

const addMenuItems = computed<LinkMenuItem[]>(() =>
  availablePlatforms.value.map(platform => ({
    label: platform.label,
    iconName: platform.icon,
    command: () => addPlatform(platform.field),
  })),
)

const addPlatform = async (field: SubKreweLinkField) => {
  if (shownFields.value.includes(field)) return

  shownFields.value.push(field)
  removedFields.value = removedFields.value.filter(f => f !== field)

  await nextTick()
  document.getElementById(idFor(field))?.focus()
  emit('added', field)
}

const removePlatform = (field: SubKreweLinkField) => {
  shownFields.value = shownFields.value.filter(f => f !== field)
  if (!removedFields.value.includes(field)) {
    removedFields.value.push(field)
  }
  model.value[field] = ''

  emit('removed', field)
}
</script>

<style scoped>
/* Reads like a KbField label, for the group of rows below it */
.kb-links-title {
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--kb-fg);
}

/* Two rows side by side where there's room */
.kb-link-rows {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(14rem, 1fr));
  gap: 1rem;
}

/* Line the text button's label up with the fields above it */
.kb-links-add {
  margin-left: -0.5rem;
}
</style>
